import { Link, useParams, Navigate } from "react-router-dom";
import SEO, { coachPersonSchema, breadcrumbSchema } from "../../lib/seo";
import { COACHES, SCHEDULE } from "../../data/content";
import { LOCATIONS } from "../../data/site";
import { Reveal, Eyebrow, TrialButton, SectionShell } from "../../components/shared";
import { CoachMonogram } from "../../components/interactive";

export default function CoachPage() {
  const { slug } = useParams();
  const c = COACHES.find((x) => x.slug === slug);
  if (!c) return <Navigate to="/coaches" replace />;
  const classes = SCHEDULE.filter((s) => s.coach === c.name);
  const others = COACHES.filter((x) => x.slug !== slug);
  return (
    <>
      <SEO
        title={`${c.name} | ${c.role} | Mezger Martial Arts at Hidden Gym`}
        description={`${c.card} Book a free trial class with ${c.name.split(" ")[0]} at Hidden Gym Richardson or North Dallas.`}
        schemas={[coachPersonSchema(c), breadcrumbSchema([["Home", "/"], ["Coaches", "/coaches"], [c.name, `/coaches/${c.slug}`]])]}
      />
      <section className="relative bg-[#0A0B0D] text-white pt-24 pb-16 overflow-hidden grain">
        {c.image && <img src={c.image} alt="" className="absolute inset-0 w-full h-full object-cover object-top opacity-20" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/60 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>{c.role}</Eyebrow>
            <h1 className="mt-3 font-display font-black uppercase tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.92]" data-testid="coach-page-name">
              {c.name.split(" ")[0]}<br /><span className="text-neutral-400">{c.name.split(" ").slice(1).join(" ")}</span>
            </h1>
            <ul className="mt-6 flex flex-wrap gap-2">
              {c.credentials.map((cr) => (
                <li key={cr} className="text-xs font-bold uppercase tracking-wide bg-white/10 border border-white/15 px-3 py-1.5">{cr}</li>
              ))}
            </ul>
            <div className="mt-8">
              <TrialButton campaign={`coach-${c.slug}`} page={`coach-${c.slug}`} testId="coach-page-trial-btn">
                Book a Trial With {c.name.split(" ")[0]}'s Team
              </TrialButton>
            </div>
          </Reveal>
        </div>
      </section>

      <SectionShell className="bg-white">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <Eyebrow>The Story</Eyebrow>
              <h2 className="mt-2 font-display font-black uppercase text-3xl sm:text-4xl">How {c.name.split(" ")[0]} Got Here</h2>
              <div className="mt-6 space-y-5 text-[#374151] leading-relaxed">
                {c.story.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              {c.favorite && <p className="mt-6 text-sm text-[#5A6270]"><strong>Favorite class to teach:</strong> {c.favorite}</p>}
            </Reveal>
          </div>
          <aside className="space-y-6">
            <Reveal>
              {c.image ? (
                <img src={c.image} alt={`${c.name}, ${c.role} at Mezger Martial Arts`} className="w-full aspect-[4/3] object-cover object-top border border-black/10" />
              ) : (
                <div className="w-full aspect-[4/3] border border-black/10"><CoachMonogram coach={c} /></div>
              )}
            </Reveal>
            <Reveal>
              <div className="bg-[#0A0B0D] text-white p-6">
                <h3 className="font-display font-black uppercase text-xl">Train With {c.name.split(" ")[0]}</h3>
                <p className="mt-2 text-white/70 text-sm">First class free at Hidden Gym. Prefer one-on-one? <Link to="/programs/private-lessons" className="underline hover:text-white" data-testid="coach-page-private-link">Request a private lesson</Link> and note {c.name.split(" ")[0]}'s name.</p>
                <TrialButton campaign={`coach-side-${c.slug}`} page={`coach-${c.slug}`} testId="coach-page-sidebar-trial-btn" className="mt-4 w-full">Book My Free Trial</TrialButton>
              </div>
            </Reveal>
            {c.disciplines.length > 0 && (
              <Reveal>
                <div className="border border-black/10 p-6 text-sm">
                  <h3 className="font-display font-black uppercase text-lg mb-2">Disciplines</h3>
                  <p className="text-[#374151]">{c.disciplines.join(" · ")}</p>
                </div>
              </Reveal>
            )}
          </aside>
        </div>
      </SectionShell>

      {c.lineage.length > 0 && (
      <SectionShell dark>
        <Reveal>
          <Eyebrow>Lineage & Titles</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase text-3xl sm:text-4xl">The Record</h2>
        </Reveal>
        <ol className="mt-8 grid sm:grid-cols-2 gap-px bg-white/10" data-testid="coach-lineage-list">
          {c.lineage.map((l, i) => (
            <li key={l} className="bg-[#0A0B0D] p-5 flex gap-4 items-start">
              <span className="font-display font-black text-2xl text-white/30 leading-none">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-white/75 text-sm leading-relaxed">{l}</span>
            </li>
          ))}
        </ol>
      </SectionShell>
      )}

      {classes.length > 0 && (
        <SectionShell className="bg-white">
          <Reveal>
            <Eyebrow>On the Schedule</Eyebrow>
            <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl">Classes Led by {c.name.split(" ")[0]}'s Program</h2>
            <p className="mt-2 text-xs font-mono text-[#5A6270]">From the last published schedule — confirm current times with Hidden Gym.</p>
          </Reveal>
          <div className="mt-6 border border-black/10 divide-y divide-black/5">
            {classes.map((s, i) => (
              <div key={i} className="grid grid-cols-2 md:grid-cols-4 gap-2 p-4 items-center">
                <span className="font-display font-black uppercase">{s.day} <span className="text-neutral-400">{s.time}</span></span>
                <Link to={`/programs/${s.slug}`} className="font-bold hover:text-neutral-500">{s.label}</Link>
                <span className="text-sm text-[#5A6270]">{s.age}</span>
                <span className="text-sm text-[#5A6270]">{LOCATIONS.find((l) => l.slug === s.loc)?.short}</span>
              </div>
            ))}
          </div>
        </SectionShell>
      )}

      <SectionShell>
        <Reveal>
          <Eyebrow>The Rest of the Corner</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl">More Coaches</h2>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-3 gap-6">
          {others.map((o) => (
            <Link key={o.slug} to={`/coaches/${o.slug}`} data-testid={`coach-related-${o.slug}`} className="group bg-white border border-black/10 hover:border-[#0A0B0D] transition-colors overflow-hidden">
              <div className="aspect-video overflow-hidden">
                {o.image ? (
                  <img src={o.image} alt={`${o.name}, ${o.role} at Mezger Martial Arts`} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <CoachMonogram coach={o} testIdPrefix="coach-related-monogram" />
                )}
              </div>
              <div className="p-4">
                <h3 className="font-display font-black uppercase text-lg">{o.name}</h3>
                <p className="text-xs font-mono uppercase tracking-widest text-neutral-400 mt-1">{o.role}</p>
              </div>
            </Link>
          ))}
        </div>
      </SectionShell>
    </>
  );
}
